/* =========================================================
   CHAGUAL — Contrato transversal de los cinco filtros propios

   Qué es: la capa COMPARTIDA de teclado, foco, peticiones, estado vacío y
   medición que usan los cinco owners de filtro (Maceteros, Muebles, Sale,
   Sale Maceteros y Sale Muebles).

   Qué NO es: un sexto filtro. No decide por handle, no arma URLs, no lee
   facetas, no reemplaza resultados, no renderiza grilla ni tarjeta. Cada
   colección conserva su owner separado (AGENTS.md §2); este archivo solo les
   presta funciones que antes estaban copiadas —o faltaban— cinco veces.

   Origen: auditoría 16 §6, auditoría 22 §5 «Lote G» (B7, B9, B10, B11, B12) y
   auditoría 24 §7 «Lote M3» (huecos 1, 2, 3 y 5).

   Los cinco controladores funcionan igual si este archivo no carga: todas las
   llamadas pasan por un puente tolerante en cada owner.
   ========================================================= */
(function () {
  'use strict';

  var DEFAULT_ARROWS = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft'];
  var EVENT_NAME = 'chagual_filter';
  var PENDING_KEY = 'chagual:filter-pending';

  // ---------------------------------------------------------------
  // B10 — Estado ocupado con guarda de secuencia.
  // Modelo copiado de assets/chagual-sale-root-filter.js, el único de los cinco
  // que ya retiraba aria-busy solo si la respuesta seguía siendo la vigente.
  // Sin la guarda, una petición vieja da por terminada la carga que puso otra.
  // ---------------------------------------------------------------
  function beginBusy(root) {
    if (root && root.setAttribute) root.setAttribute('aria-busy', 'true');
  }

  function endBusy(root, isCurrent) {
    if (root && isCurrent && root.removeAttribute) root.removeAttribute('aria-busy');
  }

  // ---------------------------------------------------------------
  // B9 — Teclado completo en los grupos que se anuncian como botones de opción.
  // Un grupo de radios debe ser UNA parada de tabulación y moverse con flechas,
  // Inicio y Fin. Antes Maceteros y Muebles no respondían a las flechas y los
  // cinco dejaban una parada por opción (doce en Maceteros).
  // ---------------------------------------------------------------
  function enabledRadios(group) {
    return Array.prototype.slice.call(group.querySelectorAll('[role="radio"]'))
      .filter(function (item) { return item.getAttribute('aria-disabled') !== 'true'; });
  }

  // tabindex rotatorio: la opción marcada es la única parada del grupo.
  // No mueve el foco: solo reparte tabindex, para no robarle el foco a nadie.
  function refreshRoving(scope) {
    if (!scope || !scope.querySelectorAll) return;
    Array.prototype.forEach.call(scope.querySelectorAll('[role="radiogroup"]'), function (group) {
      var all = Array.prototype.slice.call(group.querySelectorAll('[role="radio"]'));
      if (!all.length) return;
      var active = null;
      all.forEach(function (item) {
        if (!active && item.getAttribute('aria-checked') === 'true' && item.getAttribute('aria-disabled') !== 'true') active = item;
      });
      if (!active) active = enabledRadios(group)[0] || all[0];
      all.forEach(function (item) { item.setAttribute('tabindex', item === active ? '0' : '-1'); });
    });
  }

  // `onSelect(next, group)` lo implementa cada owner: es quien conoce su estado,
  // su vocabulario y su render. El contrato solo resuelve qué opción sigue.
  // `keys` es la lista de flechas que declara el owner; el orden es
  // [avanzar, avanzar, retroceder, retroceder].
  function bindRadioKeyboard(scope, onSelect, keys) {
    if (!scope || !scope.addEventListener || typeof onSelect !== 'function') return;
    var arrows = keys && keys.length === 4 ? keys : DEFAULT_ARROWS;
    scope.addEventListener('keydown', function (event) {
      var isArrow = arrows.indexOf(event.key) !== -1;
      var isEdge = event.key === 'Home' || event.key === 'End';
      if (!isArrow && !isEdge) return;
      var radio = event.target && event.target.closest ? event.target.closest('[role="radio"]') : null;
      var group = radio && radio.closest ? radio.closest('[role="radiogroup"]') : null;
      if (!radio || !group) return;
      var items = enabledRadios(group);
      var index = items.indexOf(radio);
      if (index === -1 || items.length < 2) return;
      event.preventDefault();
      var next;
      if (event.key === 'Home') next = items[0];
      else if (event.key === 'End') next = items[items.length - 1];
      else {
        var step = arrows.indexOf(event.key) < 2 ? 1 : -1;
        next = items[(index + step + items.length) % items.length];
      }
      if (!next) return;
      onSelect(next, group);
      refreshRoving(scope);
      next.setAttribute('tabindex', '0');
      if (typeof next.focus === 'function') next.focus();
    });
  }

  // ---------------------------------------------------------------
  // B12 — Foco al quitar un filtro aplicado.
  // Modelo copiado de assets/chagual-maceteros-filter.js, el único de los cinco
  // que ya lo hacía: al desaparecer el chip pulsado, el foco pasa al chip
  // siguiente y, si no queda ninguno, al botón que abre el cajón.
  // ---------------------------------------------------------------
  function focusAfterChipRemoval(row, chipSelector, fallback) {
    var next = row && row.querySelector ? row.querySelector(chipSelector) : null;
    var target = next || fallback;
    if (target && typeof target.focus === 'function') {
      try { target.focus({ preventScroll: true }); } catch (error) { target.focus(); }
    }
  }

  // ---------------------------------------------------------------
  // Medición (auditoría 24, huecos 1, 2, 3 y 5).
  // Destino: el flujo ADMINISTRADO que ya existe. `Shopify.analytics.publish`
  // es la vía oficial de Eventos de cliente de Shopify —la instala el propio
  // Web Pixels Manager— y es la única salida del theme que no reintroduce un
  // cargador de etiquetas. NO se escribe en window.dataLayer: la auditoría 24
  // §5.6 comprobó que ese buzón no tiene lector (no hay contenedor GTM).
  // ---------------------------------------------------------------
  function publish(payload) {
    try {
      var analytics = window.Shopify && window.Shopify.analytics;
      if (analytics && typeof analytics.publish === 'function') analytics.publish(EVENT_NAME, payload);
    } catch (error) { /* la medición nunca puede romper la vitrina */ }
    // Señal interna del theme, para consumidores propios ya existentes.
    try {
      if (typeof window.CustomEvent === 'function') {
        document.dispatchEvent(new CustomEvent('chagual:filter-tracked', { detail: payload }));
      }
    } catch (error) { /* idem */ }
  }

  function facetsKey(facets) {
    if (!facets || !facets.length) return '';
    return facets.map(function (item) { return item.facet + ':' + item.value; }).join('|');
  }

  function basePayload(owner, facets, results, url) {
    var list = facets || [];
    return {
      owner: owner || '',
      collection: owner || '',
      facets: list,
      facets_key: facetsKey(list),
      facet: list.length === 1 ? list[0].facet : '',
      value: list.length === 1 ? list[0].value : '',
      facet_count: list.length,
      results: typeof results === 'number' && results >= 0 ? results : null,
      url: url || (window.location ? window.location.pathname + window.location.search : '')
    };
  }

  // Hueco 1 — aplicar filtro con faceta, valor y cantidad de resultados.
  function trackApply(owner, facets, results, url) {
    var payload = basePayload(owner, facets, results, url);
    payload.action = 'apply';
    publish(payload);
    if (payload.results === 0) trackZeroResults(owner, facets, url);
  }

  // Hueco 2 — limpiar filtros deja de ser indistinguible de una llegada normal.
  function trackClear(owner, results, url, sourceName) {
    var payload = basePayload(owner, [], results, url);
    payload.action = 'clear';
    payload.source = sourceName || 'drawer';
    publish(payload);
  }

  // Hueco 3 — cero resultados: demanda con intención de compra que hoy se pierde.
  function trackZeroResults(owner, facets, url) {
    var payload = basePayload(owner, facets, 0, url);
    payload.action = 'zero_results';
    publish(payload);
  }

  // Hueco 5 — la caída al camino lento deja de ser silenciosa.
  // Como el fallback es una recarga real, el envío inmediato puede perderse:
  // se deja anotado y el propio contrato lo publica al cargar la página nueva.
  // No es un segundo canal: la publicación sigue siendo la administrada.
  function trackFallback(owner, reason, url) {
    var payload = basePayload(owner, [], null, url);
    payload.action = 'fallback';
    payload.reason = reason || 'desconocido';
    try {
      if (window.sessionStorage) window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(payload));
    } catch (error) { publish(payload); }
  }

  function drainPending() {
    var raw = null;
    try {
      if (!window.sessionStorage) return;
      raw = window.sessionStorage.getItem(PENDING_KEY);
      if (raw) window.sessionStorage.removeItem(PENDING_KEY);
    } catch (error) { return; }
    if (!raw) return;
    try { publish(JSON.parse(raw)); } catch (error) { /* dato ilegible: se descarta */ }
  }

  // ---------------------------------------------------------------
  // B7 — Estado vacío: registrar la llegada servida con cero resultados.
  // El bloque lo pinta el servidor (snippets/chagual-filter-empty-state.liquid),
  // así que existe aunque el JavaScript no corra. Aquí solo se mide.
  // ---------------------------------------------------------------
  function reportServedEmptyState() {
    var empty = document.querySelector('[data-ch-filter-empty][data-ch-filter-empty-owner]');
    if (!empty) return;
    var owner = empty.getAttribute('data-ch-filter-empty-owner') || '';
    var key = empty.getAttribute('data-ch-filter-empty-facets') || '';
    var facets = key ? key.split('|').filter(Boolean).map(function (pair) {
      var parts = pair.split(':');
      return { facet: parts[0] || '', value: parts.slice(1).join(':') };
    }) : [];
    trackZeroResults(owner, facets, window.location.pathname + window.location.search);
  }

  // «Limpiar filtros» y las demás salidas del estado vacío son enlaces normales:
  // funcionan sin JavaScript. Aquí solo se anota que la persona usó la salida.
  function bindEmptyStateActions() {
    document.addEventListener('click', function (event) {
      var action = event.target && event.target.closest ? event.target.closest('[data-ch-filter-empty-action]') : null;
      if (!action) return;
      var host = action.closest('[data-ch-filter-empty]');
      var owner = host ? host.getAttribute('data-ch-filter-empty-owner') || '' : '';
      var name = action.getAttribute('data-ch-filter-empty-action') || '';
      if (name === 'clear') {
        trackClear(owner, null, action.getAttribute('href') || '', 'empty_state');
        return;
      }
      var payload = basePayload(owner, [], null, action.getAttribute('href') || '');
      payload.action = 'empty_state_exit';
      payload.source = name;
      publish(payload);
    }, true);
  }

  window.ChagualFilterContract = {
    beginBusy: beginBusy,
    endBusy: endBusy,
    bindRadioKeyboard: bindRadioKeyboard,
    refreshRoving: refreshRoving,
    focusAfterChipRemoval: focusAfterChipRemoval,
    trackApply: trackApply,
    trackClear: trackClear,
    trackZeroResults: trackZeroResults,
    trackFallback: trackFallback
  };

  function init() {
    drainPending();
    reportServedEmptyState();
    bindEmptyStateActions();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
